import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

const ALLOWED_TAGS = new Set([
  'a', 'article', 'aside', 'b', 'blockquote', 'br', 'cite', 'code', 'div', 'em',
  'figcaption', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'header',
  'hr', 'i', 'img', 'li', 'main', 'nav', 'ol', 'p', 'picture', 'pre', 'section',
  'small', 'source', 'span', 'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr',
  'ul',
]);

const DANGEROUS_TAGS = new Set([
  'base', 'button', 'embed', 'form', 'iframe', 'input', 'link', 'meta', 'object',
  'script', 'select', 'style', 'textarea',
]);

const ALLOWED_ATTRS = new Set([
  'alt', 'aria-hidden', 'aria-label', 'class', 'colspan', 'decoding', 'height',
  'href', 'id', 'loading', 'rel', 'role', 'rowspan', 'src', 'srcset', 'target',
  'title', 'type', 'width',
]);

const URL_ATTRS = new Set(['href', 'src', 'srcset']);
const JS_SCHEME = String.fromCharCode(106, 97, 118, 97, 115, 99, 114, 105, 112, 116, 58);
const VBS_SCHEME = String.fromCharCode(118, 98, 115, 99, 114, 105, 112, 116, 58);

const isSafeUrl = (value = '') => {
  const normalized = String(value).trim().replace(/\s+/g, '').toLowerCase();
  if (!normalized) return true;
  return !(
    normalized.includes(JS_SCHEME)
    || normalized.includes(VBS_SCHEME)
    || normalized.includes('data:text/html')
  );
};

const sanitizeElement = (element) => {
  Array.from(element.attributes || []).forEach((attr) => {
    const name = attr.name.toLowerCase();
    const value = attr.value || '';
    const allowed = ALLOWED_ATTRS.has(name) || name.startsWith('data-') || name.startsWith('aria-');

    if (!allowed || name.startsWith('on') || (URL_ATTRS.has(name) && !isSafeUrl(value))) {
      element.removeAttribute(attr.name);
    }
  });

  if (element.tagName.toLowerCase() === 'a') {
    const target = element.getAttribute('target');
    if (target === '_blank') {
      element.setAttribute('rel', 'noopener noreferrer');
    }
  }
};

export const sanitizeCustomHtml = (html = '') => {
  if (typeof document === 'undefined') return '';
  const template = document.createElement('template');
  template.innerHTML = String(html || '').slice(0, 12000);

  const walk = (node) => {
    Array.from(node.childNodes || []).forEach((child) => {
      if (child.nodeType === 8) {
        child.remove();
        return;
      }

      if (child.nodeType !== 1) return;

      const tag = child.tagName.toLowerCase();
      if (DANGEROUS_TAGS.has(tag) || !ALLOWED_TAGS.has(tag)) {
        child.remove();
        return;
      }

      sanitizeElement(child);
      walk(child);
    });
  };

  walk(template.content);
  return template.innerHTML;
};

export const sanitizeCustomCss = (css = '') => String(css || '')
  .slice(0, 8000)
  .replace(/@import[^;]+;/gi, '')
  .replace(/expression\s*\([^)]*\)/gi, '')
  .replace(/javascript\s*:/gi, '')
  .replace(/vbscript\s*:/gi, '')
  .replace(/data\s*:\s*text\/html/gi, '')
  .replace(/<\/?style\b[^>]*>/gi, '')
  .replace(/<\/?script\b[^>]*>/gi, '')
  .trim();

export const CustomHtmlStyles = ({ settings }) => {
  const css = useMemo(() => sanitizeCustomCss(settings?.customCss), [settings]);
  if (!settings?.enabled || !css) return null;
  return <style data-custom-html-css>{css}</style>;
};

const DYNAMIC_BLOCK_RE = /\[jay-([a-z0-9-]+)\]/gi;

const renderHtmlChunk = (chunk, key) => {
  const html = sanitizeCustomHtml(chunk);
  if (!html) return null;

  return (
    <div
      className="jay-custom-html__chunk"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
      key={key}
    />
  );
};

const renderDynamicHtml = (html = '', dynamicBlocks = {}) => {
  const output = [];
  let lastIndex = 0;
  let match;

  DYNAMIC_BLOCK_RE.lastIndex = 0;
  while ((match = DYNAMIC_BLOCK_RE.exec(html)) !== null) {
    const [token, blockName] = match;
    const before = html.slice(lastIndex, match.index);
    const block = dynamicBlocks?.[blockName];

    output.push(renderHtmlChunk(before, `html-${lastIndex}`));

    if (block) {
      output.push(
        <div className={`jay-dynamic-block jay-dynamic-block--${blockName}`} data-jay-block={token} key={`block-${match.index}`}>
          {typeof block === 'function' ? block() : block}
        </div>
      );
    } else {
      output.push(renderHtmlChunk(token, `unknown-${match.index}`));
    }

    lastIndex = match.index + token.length;
  }

  output.push(renderHtmlChunk(html.slice(lastIndex), `html-${lastIndex}`));

  return output.filter(Boolean);
};

export const CustomHtmlSection = ({ settings, section, dynamicBlocks }) => {
  const rawHtml = settings?.[section] || '';
  const html = useMemo(() => sanitizeCustomHtml(rawHtml), [rawHtml]);
  const hasDynamicBlocks = useMemo(() => {
    DYNAMIC_BLOCK_RE.lastIndex = 0;
    return DYNAMIC_BLOCK_RE.test(rawHtml);
  }, [rawHtml]);
  if (!settings?.enabled || !html) return null;

  if (hasDynamicBlocks) {
    return (
      <section className={`jay-custom-html jay-custom-html--${section}`}>
        {renderDynamicHtml(rawHtml, dynamicBlocks)}
      </section>
    );
  }

  return (
    <section
      className={`jay-custom-html jay-custom-html--${section}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

const settingsShape = PropTypes.shape({
  enabled: PropTypes.bool,
  beforeHero: PropTypes.string,
  fullHome: PropTypes.string,
  mode: PropTypes.string,
  afterHero: PropTypes.string,
  beforeProducts: PropTypes.string,
  beforeFooter: PropTypes.string,
  customCss: PropTypes.string,
});

CustomHtmlStyles.propTypes = {
  settings: settingsShape,
};

CustomHtmlStyles.defaultProps = {
  settings: null,
};

CustomHtmlSection.propTypes = {
  settings: settingsShape,
  section: PropTypes.oneOf(['fullHome', 'beforeHero', 'afterHero', 'beforeProducts', 'beforeFooter']).isRequired,
  dynamicBlocks: PropTypes.shape(),
};

CustomHtmlSection.defaultProps = {
  settings: null,
  dynamicBlocks: {},
};
